"use client"
import { TextProps } from "@/elements/Text/Text"
import { Text } from "@/elements/Text"
import { DateTime, Duration } from "luxon"
import React, { useEffect, useRef, useState } from "react"

interface CountdownTimerProps extends TextProps {
  expiresAt: string
  displayVariant?: "clock" | "text"
  onExpire?: () => void
}

/*
 * Returns a Text component with a countdown timer.
 * clock format: 04:23
 * text format: 4hrs 23m
 */
export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  expiresAt,
  displayVariant = "clock",
  onExpire,
  ...props
}) => {
  const [timeLeft, setTimeLeft] = useState<Duration | null>(null)
  const hasFired = useRef(false)

  useEffect(() => {
    const target = DateTime.fromISO(expiresAt)

    const updateCountdown = () => {
      const now = DateTime.now()
      const diff = target.diff(now, ["hours", "minutes"])
      const duration = diff.toMillis() > 0 ? diff : Duration.fromMillis(0)
      setTimeLeft(duration)

      if (duration.toMillis() === 0 && !hasFired.current) {
        hasFired.current = true
        onExpire?.()
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [expiresAt, onExpire])

  if (!timeLeft) return null

  const { hours, minutes } = timeLeft.toObject()
  const hrs = Math.floor(hours ?? 0)
  const mins = Math.floor(minutes ?? 0)

  const formatted =
    displayVariant === "clock"
      ? `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`
      : `${hrs}hrs ${mins}m`

  return <Text {...props}>{formatted}</Text>
}
